// The call screen in the approved Sept 13 look (ported from ottos-consult
// src/consult/CallStage.jsx, PORTED-FROM.md): Otto's stage over the office
// backdrop, a compact "you" card, big captions in the lower third, a control
// tray. v2 adds (design 7.5 to 7.8, Wes's Sept 28 decisions): the agenda, the
// video or voice-only toggle, the "Transcript on" marker, the voice-only AI
// disclosure, reconnecting and resume states, and a confirm before End. The old
// countdown pill is gone: the clock is a guide in the agenda, never a countdown.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Captions, CaptionsOff, Mic, MicOff, PhoneOff } from "lucide-react";
import type { AvatarFallbackReason, AvatarStats } from "../avatar/OttoAvatar";
import type { ModelVariant } from "../avatar/models";
import type { Mood } from "../avatar/avatarMotion";
import type { ConsultStrings } from "../strings";
import { AgendaPanel } from "./AgendaPanel";
import { CaptionOverlay } from "./CaptionOverlay";
import type { CallController, CallView } from "./callController";
import { ModeToggle } from "./ModeToggle";
import { OttoStage } from "./OttoStage";

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((part) => (part[0] ?? "").toUpperCase())
    .join("");
}

function LevelBars({
  bandsRef,
  active,
  tone = "gold",
}: {
  bandsRef: { current: number[] };
  active: boolean;
  tone?: "gold" | "blue";
}) {
  const barsRef = useRef<(HTMLSpanElement | null)[]>([]);
  useEffect(() => {
    let frame = 0;
    const tick = () => {
      const bands = bandsRef.current;
      barsRef.current.forEach((bar, index) => {
        if (!bar) return;
        let level = 0;
        if (active) {
          const start = Math.floor((bands.length * index) / 4);
          const end = Math.max(start + 1, Math.floor((bands.length * (index + 1)) / 4));
          let total = 0;
          for (let band = start; band < end; band += 1) total += bands[band] ?? 0;
          level = total / Math.max(1, end - start);
        }
        bar.style.transform = `scaleY(${0.26 + level * 0.74})`;
      });
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [active, bandsRef]);
  return (
    <span className={`consult-call-hearing-bars is-${tone}`} aria-hidden="true">
      {Array.from({ length: 4 }, (_, index) => (
        <span
          key={index}
          ref={(node) => {
            barsRef.current[index] = node;
          }}
        />
      ))}
    </span>
  );
}

function TrayButton({
  children,
  label,
  ariaLabel,
  active = false,
  onClick,
}: {
  children: ReactNode;
  label: string;
  ariaLabel?: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`consult-call-tray-button${active ? " is-active" : ""}`}
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel ?? label}
    >
      <span className="consult-call-tray-button__icon" aria-hidden="true">
        {children}
      </span>
      <span className="consult-call-tray-button__label" aria-hidden="true">
        {label}
      </span>
    </button>
  );
}

function notice(
  s: ConsultStrings,
  view: CallView,
  onRejoin: () => void,
): { tone: "info" | "error"; text: string; spinner?: boolean; action?: { label: string; run: () => void } } | null {
  if (view.phase === "reconnecting") return { tone: "info", text: s.call.reconnecting, spinner: true };
  if (view.phase === "lost")
    return { tone: "error", text: s.call.lost, action: { label: s.call.rejoin, run: onRejoin } };
  if (view.serverRecovering) return { tone: "info", text: s.call.serverRecovering, spinner: true };
  return null;
}

export interface CallStageProps {
  s: ConsultStrings;
  view: CallView;
  controller: CallController;
  firstName: string;
  variant: ModelVariant;
  statsRef: { current: AvatarStats & { loadMs: number | null; fellBack: boolean } };
  onFallback: (reason: AvatarFallbackReason) => void;
  moodRef?: { current: Mood };
}

export function CallStage({ s, view, controller, firstName, variant, statsRef, onFallback, moodRef }: CallStageProps) {
  const [captionsOn, setCaptionsOn] = useState(true);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [fellBack, setFellBack] = useState(false);
  const [retry3d, setRetry3d] = useState(0);
  const micLive = !view.muted && (view.micState === "capturing" || view.micState === "starting");
  const ending = view.phase === "ending" || view.phase === "ended";
  const statusWord =
    view.ottoState === "speaking"
      ? s.call.speaking
      : view.ottoState === "thinking"
        ? s.call.thinking
        : view.ottoState === "listening"
          ? s.call.listening
          : s.call.ready;
  const ottoLine = `${s.call.ottoPill} · ${statusWord}`;
  const current = notice(s, view, () => void controller.rejoin());
  const voiceOnly = view.mode === "voice";

  // Status changes that matter are announced once (design 7.7), not every listening or thinking change.
  const announce =
    view.phase === "reconnecting"
      ? s.call.reconnecting
      : view.phase === "lost"
        ? s.call.lost
        : view.phase === "ending"
          ? s.call.ending
          : view.phase === "live"
            ? s.call.live
            : "";

  return (
    <main className={`consult-call-stage${voiceOnly ? " is-voice" : " is-video"}`}>
      <div className="consult-call-scene">
        <OttoStage
          mode={view.mode}
          ottoState={view.ottoState}
          stateRef={controller.ottoStateRef}
          player={controller.player}
          variant={variant}
          statusLabel={ottoLine}
          statsRef={statsRef}
          retryKey={retry3d}
          {...(moodRef ? { moodRef } : {})}
          cardNotes={
            voiceOnly ? (
              <div className="consult-voice-notes">
                <p>{s.call.voiceOnlyDisclosure}</p>
                <p>{s.call.keepScreenOpen}</p>
              </div>
            ) : fellBack ? (
              <p className="consult-voice-notes">{s.call.avatarFallback}</p>
            ) : null
          }
          onFallback={(reason) => {
            setFellBack(true);
            onFallback(reason);
          }}
        />

        <div className="consult-call-bottom-gradient" aria-hidden="true" />

        {/* Wes, Oct 14 2026: the logo with Otto's status beside it; no title, step or "live, transcript on" line. */}
        <header className="consult-call-header" aria-label={s.call.headerTitle}>
          <div className="consult-call-header__title">
            <img src="/images/consult/logo-badge-cream-on-blue.png" alt={s.site.brandAlt} />
            <div className="consult-call-otto-pill">
              <span aria-hidden="true" />
              {/* On a phone only the status word shows, so the pill never runs under your chip. */}
              <b aria-label={ottoLine}>
                <i className="consult-call-otto-pill__name">{s.call.ottoPill} · </i>
                {statusWord}
              </b>
            </div>
          </div>
          <div
            className="consult-call-camera-card"
            aria-label={`${s.call.you}, ${micLive ? s.call.micOn : s.call.micOff}`}
          >
            <div className="consult-call-camera-card__body">
              <span className="consult-call-camera-card__initials">{initialsFor(firstName)}</span>
              <b>{s.call.cameraOff}</b>
            </div>
            <div className="consult-call-camera-card__footer">
              <LevelBars bandsRef={controller.micBandsRef} active={micLive} tone="blue" />
              <span>{s.call.you}</span>
              <b>{micLive ? s.call.micOn : s.call.micOff}</b>
            </div>
          </div>
        </header>

        <AgendaPanel
          s={s}
          items={view.agenda}
          index={view.agendaIndex}
          clock={view.clock}
          targetMinutes={view.targetMinutes}
          extension={view.extension}
          onAnswer={(accept) => controller.answerExtension(accept)}
        />

        {captionsOn && !current && (
          <CaptionOverlay
            text={view.caption.text}
            startedAt={view.caption.startedAt}
            speaking={view.ottoState === "speaking"}
          />
        )}

        {current && (
          <div
            className={`consult-call-notice is-${current.tone}`}
            role={current.tone === "error" ? "alert" : "status"}
          >
            {current.spinner && <span className="consult-call-notice__spinner" aria-hidden="true" />}
            <span>{current.text}</span>
            {current.action && (
              <button type="button" onClick={current.action.run}>
                {current.action.label}
              </button>
            )}
          </div>
        )}

        {view.audioBlocked && (
          <button type="button" className="consult-call-unmute" onClick={() => void controller.unlockAudio()}>
            {s.call.unmuteOtto}
          </button>
        )}

        {confirmEnd && !ending && (
          <div className="consult-end-confirm" role="alertdialog" aria-labelledby="consult-end-confirm-text">
            <p id="consult-end-confirm-text">{s.call.endConfirm}</p>
            <div>
              <button
                type="button"
                className="is-end"
                onClick={() => {
                  setConfirmEnd(false);
                  controller.end();
                }}
              >
                {s.call.endConfirmYes}
              </button>
              <button type="button" onClick={() => setConfirmEnd(false)} autoFocus>
                {s.call.endConfirmNo}
              </button>
            </div>
          </div>
        )}

        <div className="consult-call-control-tray" aria-label={s.call.headerTitle}>
          <div className="consult-call-hearing">
            <LevelBars bandsRef={controller.micBandsRef} active={micLive} />
            <span>{micLive ? s.call.canHear : s.call.muted}</span>
          </div>
          <span className="consult-call-control-divider" aria-hidden="true" />
          <div className="consult-call-action-group">
            <TrayButton
              label={view.muted ? s.call.unmute : s.call.mute}
              active={view.muted}
              onClick={() => controller.setMuted(!view.muted)}
            >
              {view.muted ? <MicOff size={20} strokeWidth={2.2} /> : <Mic size={20} strokeWidth={2.2} />}
            </TrayButton>
            <ModeToggle
              s={s}
              mode={view.mode}
              fellBack={fellBack}
              onChange={(mode) => {
                // Video after a fallback (or from voice only) tries the 3D Otto again (Wes, Oct 14 2026).
                if (mode === "video" || fellBack) {
                  setFellBack(false);
                  setRetry3d((n) => n + 1);
                }
                if (mode !== view.mode) controller.setMode(mode);
              }}
            />
            <TrayButton
              label={s.call.captions}
              ariaLabel={s.call.captionsOff}
              active={captionsOn}
              onClick={() => setCaptionsOn((on) => !on)}
            >
              {captionsOn ? <Captions size={20} strokeWidth={2.2} /> : <CaptionsOff size={20} strokeWidth={2.2} />}
            </TrayButton>
          </div>
          <span className="consult-call-control-divider" aria-hidden="true" />
          <button
            type="button"
            className="consult-call-save-plan is-live"
            disabled={ending}
            onClick={() => setConfirmEnd(true)}
            aria-label={ending ? s.call.ending : s.call.end}
          >
            <PhoneOff className="consult-call-save-plan__icon" size={17} strokeWidth={2.4} aria-hidden="true" />
            <span className="consult-call-save-plan__long" aria-hidden="true">
              {ending ? s.call.ending : s.call.end}
            </span>
            <span className="consult-call-save-plan__short" aria-hidden="true">
              {ending ? s.call.ending : s.call.endShort}
            </span>
          </button>
        </div>
        <p className="consult-sr-only" role="status" aria-live="polite">
          {announce}
        </p>
      </div>
      <div className="consult-call-bottom-band" aria-hidden="true" />
    </main>
  );
}
